/**
 * RequireHostedAuth.tsx
 * 
 * A wrapper component that redirects unauthenticated users to the hosted auth flow.
 * Used for pages that require user authentication via the HostedAuthContext.
 * 
 * Usage:
 * <RequireHostedAuth redirectPath="/your/protected/path" defaultView="signin">
 *   <YourProtectedComponent />
 * </RequireHostedAuth>
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHostedAuth } from '../HostedAuthContext';
import { AlyzeLoadingSpinner } from '../../../componentsV1/shared';
import { makeStyles } from '@fluentui/react-components';

const useStyles = makeStyles({
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: '16px',
        padding: '24px',
    }
});

interface RequireHostedAuthProps {
    /** The component(s) to render when authenticated */
    children: React.ReactNode;
    /** The path to redirect back to after auth completes */
    redirectPath: string;
    /** Default view: 'signin' or 'signup' */
    defaultView?: 'signin' | 'signup';
}

export const RequireHostedAuth: React.FC<RequireHostedAuthProps> = ({ 
    children, 
    redirectPath,
    defaultView = 'signup' 
}) => {
    const { isAuthenticated, isLoading } = useHostedAuth();
    const navigate = useNavigate();
    const styles = useStyles();

    useEffect(() => {
        // If not authenticated and not loading, redirect to signin or signup
        if (!isLoading && !isAuthenticated) {
            // Store the intended destination
            const authPath = defaultView === 'signin' 
                ? '/auth/hosted/test/signin' 
                : '/auth/hosted/test/signup';
            
            // Navigate with the return path in state
            navigate(authPath, { 
                state: { from: redirectPath },
                replace: true 
            });
        }
    }, [isLoading, isAuthenticated, redirectPath, defaultView, navigate]);

    // Show loading state while auth is initializing
    if (isLoading) {
        return (
            <div className={styles.loadingContainer}>
                <AlyzeLoadingSpinner message="Loading..." />
            </div>
        );
    }

    // Show redirecting state if not authenticated
    if (!isAuthenticated) {
        return (
            <div className={styles.loadingContainer}>
                <AlyzeLoadingSpinner message={`Redirecting to ${defaultView}...`} />
            </div>
        );
    }

    // Only render children if authenticated
    return <>{children}</>;
};
